import clsx from 'clsx';

import type { DecisionDimension, PersonaDecisions } from '../types';

export type DecisionSelectorProps = {
  dimension: DecisionDimension;
  value: string;
  onChange: (key: keyof PersonaDecisions, value: string) => void;
  className?: string;
  compact?: boolean;
};

function DecisionSelector({
  dimension,
  value,
  onChange,
  className,
  compact = false,
}: DecisionSelectorProps) {
  const selectedIndex = dimension.levels.findIndex((level) => level.value === value);

  return (
    <div className={clsx('space-y-3', className)}>
      {/* Question */}
      <label className="block text-sm font-medium text-gray-700 dark:text-dark-200">
        {dimension.question}
      </label>

      {/* Levels */}
      <div className="space-y-1">
        {dimension.levels.map((level, index) => {
          const isSelected = level.value === value;
          const isPast = index < selectedIndex;

          return (
            <button
              key={level.value}
              type="button"
              onClick={() => onChange(dimension.key, level.value)}
              className={clsx(
                'w-full text-start rounded-lg border-2 transition-all',
                compact ? 'px-3 py-2' : 'px-4 py-3',
                isSelected
                  ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20'
                  : isPast
                    ? 'border-gray-200 bg-gray-50 dark:border-dark-600 dark:bg-dark-700/50'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-dark-600 dark:bg-dark-800 dark:hover:border-dark-500 dark:hover:bg-dark-700'
              )}
            >
              <div className="flex items-center gap-3">
                {/* Level indicator */}
                <div
                  className={clsx(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium',
                    isSelected
                      ? 'bg-primary-500 text-white dark:bg-primary-400'
                      : isPast
                        ? 'bg-gray-300 text-gray-600 dark:bg-dark-500 dark:text-dark-300'
                        : 'bg-gray-200 text-gray-500 dark:bg-dark-600 dark:text-dark-400'
                  )}
                >
                  {index + 1}
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1">
                  <div
                    className={clsx(
                      'font-medium',
                      compact ? 'text-sm' : 'text-sm',
                      isSelected
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-gray-900 dark:text-dark-100'
                    )}
                  >
                    {level.label}
                  </div>
                  {!compact && (
                    <div
                      className={clsx(
                        'text-xs mt-0.5',
                        isSelected
                          ? 'text-primary-600 dark:text-primary-400'
                          : 'text-gray-500 dark:text-dark-400'
                      )}
                    >
                      {level.description}
                    </div>
                  )}
                </div>

                {/* Selected indicator */}
                {isSelected && (
                  <div className="shrink-0">
                    <svg
                      className="h-5 w-5 text-primary-500 dark:text-primary-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Visual scale indicator */}
      <div className="flex items-center gap-1 pt-1">
        {dimension.levels.map((level, index) => (
          <div
            key={level.value}
            className={clsx(
              'h-1.5 flex-1 rounded-full transition-colors',
              index <= selectedIndex
                ? 'bg-primary-500 dark:bg-primary-400'
                : 'bg-gray-200 dark:bg-dark-600'
            )}
          />
        ))}
      </div>
    </div>
  );
}

DecisionSelector.displayName = 'DecisionSelector';

export { DecisionSelector };
